import { activeTenantIdRef } from '@/global.shared';

import type { ServiceWorkerInMessages, ServiceWorkerOutMessages, ServiceWorkerRequests, ServiceWorkerResponses } from './sw.types';

import { Logger } from './logger';

type PageContext = ServiceWorkerInMessages['updatePageContext'];
type MessageHandler<T extends keyof ServiceWorkerOutMessages> = (data: ServiceWorkerOutMessages[T]) => void;

export class ServiceWorkerManager {
    private logger = Logger.createScoped('ServiceWorkerManager');

    private _registration: ServiceWorkerRegistration | null = null;
    private _serviceWorker: ServiceWorker | null = null;
    private _registerStarted = false;

    // Accumulated page context state
    private _pageContext: PageContext = {};

    // Generic pending requests map: requestId -> resolver function
    // oxlint-disable-next-line no-explicit-any
    private _pendingRequests: Map<string, (data: any) => void> = new Map();
    private _requestId = 0;

    // Generic message handlers: type -> Set of handlers
    // oxlint-disable-next-line no-explicit-any
    private _messageHandlers: Map<string, Set<MessageHandler<any>>> = new Map();

    get isActive() {
        return this._serviceWorker?.state === 'activated';
    }

    get registration() {
        return this._registration;
    }

    /**
     * Register the service worker. Called explicitly by the full app (useAppServiceWorker); lean
     * entries like the embedded CX app never call it, so importing this manager no longer has the
     * side effect of registering /sw.js — which 404s where no SW is shipped. The messaging methods
     * safely no-op until a SW is registered. Idempotent.
     */
    async register() {
        if (this._registerStarted) return;
        this._registerStarted = true;
        if (!('serviceWorker' in navigator)) return this.logger.warn('Service Workers are not supported in this browser.');

        navigator.serviceWorker.addEventListener('controllerchange', () => {
            this.logger.info('Service Worker controller changed.');
        });
        navigator.serviceWorker.addEventListener('message', this.handleSwMessage.bind(this));
        navigator.serviceWorker.addEventListener('messageerror', event => this.logger.error('Message error:', event));

        try {
            this.logger.info('Registering Service Worker...');
            // Register as a module so ESM imports in sw.js work
            this._registration = await navigator.serviceWorker.register('/sw.js', { type: 'module' });
        } catch (error) {
            return this.logger.error('Registration failed:', error);
        }

        this._serviceWorker = this._registration.active || this._registration.installing;
        this.logger.info('Registered successfully.');
        this.postMessage('pageLoaded', undefined);
        this.startBackgroundCheckHeartbeat();

        this._registration.addEventListener('updatefound', () => {
            this.logger.info('Service Worker update found, waiting for activation...');
            this._serviceWorker = this._registration!.installing;
            this._serviceWorker?.addEventListener('error', err => this.logger.error('Service Worker error:', err));
            this._serviceWorker?.addEventListener('statechange', () => {
                this.logger.info(`Service Worker state changed: ${this._serviceWorker!.state}`);
                if (this._serviceWorker!.state === 'activated') this.postPageContext();
            });
        });

        try {
            await this._registration.update();
        } catch (error) {
            // A failed update can keep using the registered worker; it must not become an unhandled rejection.
            this.logger.warn('Update failed; keeping the current Service Worker:', error);
        }
    }

    // The SW runs the version + incident checks, but the browser terminates it
    // when idle and its own timer dies with it. The page stays alive for the whole
    // session, so nudge the SW on an interval to drive the checks; the SW debounces
    // so that multiple tabs nudging only triggers one actual check per minute.
    private startBackgroundCheckHeartbeat() {
        window.setInterval(() => this.postMessage('runBackgroundChecks', undefined), 30_000);
    }

    private handleSwMessage(event: MessageEvent) {
        const msg = event.data as unknown;
        if (!msg || typeof msg !== 'object' || !('isZSMessage' in msg)) return;

        this.logger.info('Message received from SW:', msg);

        // oxlint-disable-next-line no-explicit-any
        const typedMsg = msg as unknown as { type: string; data: any; requestId?: string };

        // Handle responses to pending requests
        if (typedMsg.requestId) {
            const resolver = this._pendingRequests.get(typedMsg.requestId);
            if (resolver) {
                this._pendingRequests.delete(typedMsg.requestId);
                resolver(typedMsg.data);
            }
            return;
        }

        // Dispatch to registered handlers
        const handlers = this._messageHandlers.get(typedMsg.type);
        if (handlers && handlers.size > 0) {
            for (const handler of handlers) {
                handler(typedMsg.data);
            }
            return;
        }

        this.logger.warn('Unknown message type from SW:', msg);
    }

    postPageContext(updates?: Partial<PageContext>) {
        // Merge base context with accumulated state and any new updates
        this._pageContext = {
            ...this._pageContext,
            modalOpen: document.querySelector('.vf-modal') !== null,
            tenantId: activeTenantIdRef.value,
            ...updates
        };
        this.postMessage('updatePageContext', this._pageContext);
    }

    postMessage<T extends keyof ServiceWorkerInMessages>(message: T, data: ServiceWorkerInMessages[T]) {
        if (!this._serviceWorker) return this.logger.warn('No Service Worker instance to send message to.');
        this._serviceWorker.postMessage({ isZSMessage: true, type: message, data });
    }

    async sendRequest<T extends keyof ServiceWorkerRequests>(
        type: T,
        data: ServiceWorkerRequests[T],
        timeoutMs = 2000
    ): Promise<ServiceWorkerResponses[T] | null> {
        if (!this._serviceWorker) {
            this.logger.warn('No Service Worker - cannot send request');
            return null;
        }

        const requestId = `${++this._requestId}`;

        return new Promise(resolve => {
            this._pendingRequests.set(requestId, resolve);

            // Timeout handling
            setTimeout(() => {
                if (this._pendingRequests.has(requestId)) {
                    this._pendingRequests.delete(requestId);
                    this.logger.warn(`Request ${type} timed out`);
                    resolve(null);
                }
            }, timeoutMs);

            this._serviceWorker!.postMessage({ isZSMessage: true, type, data, requestId });
        });
    }

    onMessage<T extends keyof ServiceWorkerOutMessages>(type: T, handler: MessageHandler<T>): () => void {
        let handlers = this._messageHandlers.get(type);
        if (!handlers) {
            handlers = new Set();
            this._messageHandlers.set(type, handlers);
        }
        handlers.add(handler);

        // Return unsubscribe function
        return () => {
            handlers.delete(handler);
            if (handlers.size === 0) {
                this._messageHandlers.delete(type);
            }
        };
    }
}

export const serviceWorkerManager = new ServiceWorkerManager();
